// IndexedDB utility for storing SOAP notes and audio recordings

const DB_NAME = 'PearlAIDB';
const DB_VERSION = 1;
const SOAP_STORE = 'soapNotes';

/**
 * Initialize the IndexedDB database
 */
export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open database'));
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create SOAP notes store if it doesn't exist
      if (!db.objectStoreNames.contains(SOAP_STORE)) {
        const objectStore = db.createObjectStore(SOAP_STORE, {
          keyPath: 'id',
          autoIncrement: true
        });

        // Create indexes for efficient querying
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        objectStore.createIndex('patientName', 'patientName', { unique: false });
        objectStore.createIndex('appointmentDate', 'appointmentDate', { unique: false });
      }
    };
  });
};

/**
 * Save a SOAP note to IndexedDB
 * @param {Object} soapNoteData - The SOAP note data including audio blob
 * @returns {Promise<number>} - The ID of the saved note
 */
export const saveSoapNote = async (soapNoteData) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SOAP_STORE], 'readwrite');
    const objectStore = transaction.objectStore(SOAP_STORE);

    const noteToSave = {
      ...soapNoteData,
      timestamp: Date.now(),
      updatedAt: Date.now()
    };

    const request = objectStore.add(noteToSave);

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(new Error('Failed to save SOAP note'));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

/**
 * Update an existing SOAP note
 * @param {number} id - The ID of the note to update
 * @param {Object} updates - The fields to update
 * @returns {Promise<void>}
 */
export const updateSoapNote = async (id, updates) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SOAP_STORE], 'readwrite');
    const objectStore = transaction.objectStore(SOAP_STORE);

    const getRequest = objectStore.get(id);

    getRequest.onsuccess = () => {
      const note = getRequest.result;
      if (!note) {
        reject(new Error('SOAP note not found'));
        return;
      }

      const updatedNote = {
        ...note,
        ...updates,
        updatedAt: Date.now()
      };

      const updateRequest = objectStore.put(updatedNote);

      updateRequest.onsuccess = () => {
        resolve();
      };

      updateRequest.onerror = () => {
        reject(new Error('Failed to update SOAP note'));
      };
    };

    getRequest.onerror = () => {
      reject(new Error('Failed to retrieve SOAP note'));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

/**
 * Get all SOAP notes from IndexedDB
 * @returns {Promise<Array>} - Array of all SOAP notes
 */
export const getAllSoapNotes = async () => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SOAP_STORE], 'readonly');
    const objectStore = transaction.objectStore(SOAP_STORE);
    const index = objectStore.index('timestamp');

    const request = index.openCursor(null, 'prev'); // Get in reverse chronological order
    const notes = [];

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        notes.push(cursor.value);
        cursor.continue();
      } else {
        resolve(notes);
      }
    };

    request.onerror = () => {
      reject(new Error('Failed to retrieve SOAP notes'));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

/**
 * Get a single SOAP note by ID
 * @param {number} id - The ID of the note
 * @returns {Promise<Object>} - The SOAP note
 */
export const getSoapNoteById = async (id) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SOAP_STORE], 'readonly');
    const objectStore = transaction.objectStore(SOAP_STORE);

    const request = objectStore.get(id);

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(new Error('Failed to retrieve SOAP note'));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

/**
 * Delete a SOAP note by ID
 * @param {number} id - The ID of the note to delete
 * @returns {Promise<void>}
 */
export const deleteSoapNote = async (id) => {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SOAP_STORE], 'readwrite');
    const objectStore = transaction.objectStore(SOAP_STORE);

    const request = objectStore.delete(id);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(new Error('Failed to delete SOAP note'));
    };

    transaction.oncomplete = () => {
      db.close();
    };
  });
};

/**
 * Convert audio blob to base64 string for storage
 * @param {Blob} blob - The audio blob
 * @returns {Promise<string>} - Base64 encoded audio
 */
export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Convert base64 string back to blob
 * @param {string} base64 - Base64 encoded data
 * @returns {Blob} - The blob
 */
export const base64ToBlob = (base64) => {
  const parts = base64.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
};
